import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon } from 'lucide-react';
// --- REVEAL COMPONENT (Required for Footer Animations) ---
function Reveal({
  children,
  delay = 0,
  className = '',
  width = 'w-full'
}) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });
    if (ref.current) observer.observe(ref.current);
    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, []);
  return <div ref={ref} className={`transition-all duration-1000 ease-out transform ${width} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'} ${className}`} style={{
    transitionDelay: `${delay}ms`
  }} data-id="element-121">
      {children}
    </div>;
}
// --- MAIN FOOTER COMPONENT ---
export function Footer() {
  return <footer className="bg-[#496f58] text-white font-sans-brand pt-12" data-id="element-122">
      <Reveal data-id="element-123">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-8" data-id="element-124">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 border-b border-white/20 pb-10" data-id="element-125">
            {/* Company Column */}
            <div data-id="element-126">
              <h4 className="font-bold text-lg mb-4 tracking-wider" data-id="element-127">
                Faith & Fig
              </h4>
              <ul className="space-y-2" data-id="element-128">
                <li data-id="element-129">
                  <a href="https://supplement.faithandfig.com" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-130">
                    Our Mission
                  </a>
                </li>
                <li data-id="element-131">
                  <a href="https://faithandfig.com/pages/contact" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-132">
                    Contact
                  </a>
                </li>
                {/* UPGRADED POLICIES DROPDOWN */}
                <li data-id="element-133">
                  <div className="relative group" data-id="element-134">
                    <button className="text-white/80 hover:text-white transition-colors text-sm flex items-center gap-1 pb-1" data-id="element-135">
                      Policies
                      <ChevronDownIcon size={10} className="transition-transform duration-200 group-hover:rotate-180" data-id="element-136" />
                    </button>

                    {/* Dropdown Menu - Expands Upwards */}
                    <div className="absolute bottom-full left-0 mb-2 w-48 bg-white rounded-lg shadow-xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-bottom translate-y-2 group-hover:translate-y-0 z-50" data-id="element-137">
                      <a href="https://faithandfig.com/policies/privacy-policy" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#F5F3EE] hover:text-[#496f58] transition-colors" data-id="element-138">
                        Privacy Policy
                      </a>
                      <a href="https://faithandfig.com/policies/refund-policy" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#F5F3EE] hover:text-[#496f58] transition-colors" data-id="element-139">
                        Refund Policy
                      </a>
                      <a href="https://faithandfig.com/policies/shipping-policy" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#F5F3EE] hover:text-[#496f58] transition-colors" data-id="element-140">
                        Shipping Policy
                      </a>
                      <a href="https://faithandfig.com/policies/terms-of-service" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#F5F3EE] hover:text-[#496f58] transition-colors" data-id="element-141">
                        Terms of Service
                      </a>
                      {/* Tiny triangle pointer at bottom */}
                      <div className="absolute -bottom-1 left-4 w-2 h-2 bg-white transform rotate-45" data-id="element-142"></div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
            {/* Shop Column */}
            <div data-id="element-143">
              <h4 className="font-bold text-lg mb-4 tracking-wider" data-id="element-144">Shop</h4>
              <ul className="space-y-2" data-id="element-145">
                <li data-id="element-146">
                  <a href="https://pomegranates.faithandfig.com" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-147">
                    Heart Health
                  </a>
                </li>
                <li data-id="element-148">
                  <a href="https://oliveleaf.faithandfig.com" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-149">
                    Immune Defense
                  </a>
                </li>
                <li data-id="element-150">
                  <a href="https://frankincense.faithandfig.com" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-151">
                    Joint Comfort
                  </a>
                </li>
                <li data-id="element-152">
                  <a href="https://trysaffron.faithandfig.com" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-153">
                    Mood & Focus
                  </a>
                </li>
              </ul>
            </div>
            {/* Account Column */}
            <div data-id="element-154">
              <h4 className="font-bold text-lg mb-4 tracking-wider" data-id="element-155">Account</h4>
              <ul className="space-y-2" data-id="element-156">
                <li data-id="element-157">
                  <a href="https://faithandfig.com/apps/my-subscriptions" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-158">
                    Manage Subscriptions
                  </a>
                </li>
                <li data-id="element-159">
                  <a href="https://faithandfig.com/apps/track123" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-160">
                    Track Order
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6" data-id="element-161">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/70" data-id="element-162">
            <p data-id="element-163">
              © {new Date().getFullYear()} Faith & Fig. All rights reserved.
            </p>
          </div>
        </div>
      </Reveal>
    </footer>;
}